import { displayDigits } from '../utils/displayDigits';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from './NmButton';
import { MobileSheet } from './MobileSheet';
import { WheelPicker } from './workout/WheelPicker';
import {
  formatPickerPreview,
  isJalaliPickerLocale,
  jalaliKeyToPickerDate,
  pickerDateToJalaliKey,
  todayAsPickerDate,
  type CalendarPickerDate,
} from '../utils/calendarPickerLocale';
import {
  formatGregorianMonthYear,
  getDaysInGregorianMonth,
  gregorianMonthPad,
  gregorianWeekStartsOn,
  shiftGregorianMonth,
  todayGregorian,
  weekdayLabelsForGregorian,
} from '../utils/gregorianCalendar';
import {
  getDaysInJalaliMonth,
  gregorianWeekdaysToJalali,
  jalaliMonths,
  jalaliToDate,
  jalaliKeyToIso,
  shiftJalaliMonth,
  todayJalali,
  weekDaysShortFa,
} from '../utils/jalali';
import { isRtlLocale, normalizeLocale } from '../i18n/config';

function NavChevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      className="nm-date-picker__nav-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {direction === 'prev' ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

interface GridCell {
  key: string;
  day: number;
  muted?: boolean;
  today?: boolean;
  selected?: boolean;
}

export interface NmDatePickerModalProps {
  open: boolean;
  /** Canonical Jalali key `year/month/day`. */
  value: string;
  onClose: () => void;
  onConfirm: (jalaliDateKey: string) => void;
  zIndex?: number;
  portalContainer?: HTMLElement | null;
  /** Inclusive Gregorian ISO date bounds. */
  min?: string;
  max?: string;
}

function clampDay(year: number, month: number, day: number, useJalali: boolean): number {
  const max = useJalali ? getDaysInJalaliMonth(year, month) : getDaysInGregorianMonth(year, month);
  return Math.min(Math.max(day, 1), max);
}

function buildJalaliGrid(
  viewYear: number,
  viewMonth: number,
  selected: CalendarPickerDate,
): GridCell[] {
  const pad = gregorianWeekdaysToJalali(jalaliToDate(viewYear, viewMonth, 1).getDay());
  const daysInMonth = getDaysInJalaliMonth(viewYear, viewMonth);
  const today = todayJalali();
  const cells: GridCell[] = [];

  for (let i = 0; i < pad; i++) {
    cells.push({ key: `p-${i}`, day: 0, muted: true });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      key: `${viewYear}-${viewMonth}-${day}`,
      day,
      today: viewYear === today.year && viewMonth === today.month && day === today.dat,
      selected:
        viewYear === selected.year && viewMonth === selected.month && day === selected.day,
    });
  }
  return cells;
}

function buildGregorianGrid(
  viewYear: number,
  viewMonth: number,
  selected: CalendarPickerDate,
  weekStartsOn: number,
): GridCell[] {
  const pad = gregorianMonthPad(viewYear, viewMonth, weekStartsOn);
  const daysInMonth = getDaysInGregorianMonth(viewYear, viewMonth);
  const today = todayGregorian();
  const cells: GridCell[] = [];

  for (let i = 0; i < pad; i++) {
    cells.push({ key: `p-${i}`, day: 0, muted: true });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      key: `${viewYear}-${viewMonth}-${day}`,
      day,
      today: viewYear === today.year && viewMonth === today.month && day === today.day,
      selected:
        viewYear === selected.year && viewMonth === selected.month && day === selected.day,
    });
  }
  return cells;
}

/** Locale-aware bottom-sheet date picker — Jalali for fa, Gregorian for en/ar. */
export function NmDatePickerModal({ open, value, onClose, onConfirm, zIndex, min, max, portalContainer }: NmDatePickerModalProps) {
  const { t, i18n } = useTranslation();
  const localeCode = normalizeLocale(i18n.language);
  const isRtl = isRtlLocale(localeCode);
  const useJalali = isJalaliPickerLocale(localeCode);
  const weekStartsOn = gregorianWeekStartsOn(localeCode);

  const [viewYear, setViewYear] = useState(() => todayAsPickerDate(true).year);
  const [viewMonth, setViewMonth] = useState(() => todayAsPickerDate(true).month);
  const [selected, setSelected] = useState<CalendarPickerDate>(() => todayAsPickerDate(true));
  const [wheelMode, setWheelMode] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fallback = todayAsPickerDate(useJalali);
    const parsed = jalaliKeyToPickerDate(value, useJalali) ?? fallback;
    const normalized: CalendarPickerDate = {
      year: parsed.year,
      month: parsed.month,
      day: clampDay(parsed.year, parsed.month, parsed.day, useJalali),
    };
    setSelected(normalized);
    setViewYear(normalized.year);
    setViewMonth(normalized.month);
    setWheelMode(false);
  }, [open, useJalali, value]);

  const weekdayLabels = useMemo(
    () => (useJalali ? weekDaysShortFa : weekdayLabelsForGregorian(localeCode)),
    [localeCode, useJalali],
  );

  const headerTitle = useMemo(() => {
    if (useJalali) return `${jalaliMonths[viewMonth] ?? ''} ${viewYear}`;
    return formatGregorianMonthYear(viewYear, viewMonth, localeCode);
  }, [localeCode, useJalali, viewMonth, viewYear]);

  const gridCells = useMemo(() => {
    if (useJalali) return buildJalaliGrid(viewYear, viewMonth, selected);
    return buildGregorianGrid(viewYear, viewMonth, selected, weekStartsOn);
  }, [selected, useJalali, viewMonth, viewYear, weekStartsOn]);

  const yearOptions = useMemo(() => {
    const center = selected.year;
    return Array.from({ length: 21 }, (_, i) => String(center - 10 + i));
  }, [selected.year]);

  const monthOptions = useMemo(() => {
    if (useJalali) return jalaliMonths.slice(1);
    const loc = localeCode === 'ar' ? 'ar' : 'en';
    return Array.from({ length: 12 }, (_, i) =>
      new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2024, i, 1)),
    );
  }, [localeCode, useJalali]);

  const dayOptions = useMemo(() => {
    const max = useJalali
      ? getDaysInJalaliMonth(selected.year, selected.month)
      : getDaysInGregorianMonth(selected.year, selected.month);
    return Array.from({ length: max }, (_, i) => String(i + 1));
  }, [selected.month, selected.year, useJalali]);

  const preview = formatPickerPreview(selected, useJalali, localeCode);

  const goPrevMonth = useCallback(() => {
    if (useJalali) {
      const n = shiftJalaliMonth(viewYear, viewMonth, -1);
      setViewYear(n.year);
      setViewMonth(n.month);
    } else {
      const n = shiftGregorianMonth(viewYear, viewMonth, -1);
      setViewYear(n.year);
      setViewMonth(n.month);
    }
  }, [useJalali, viewMonth, viewYear]);

  const goNextMonth = useCallback(() => {
    if (useJalali) {
      const n = shiftJalaliMonth(viewYear, viewMonth, 1);
      setViewYear(n.year);
      setViewMonth(n.month);
    } else {
      const n = shiftGregorianMonth(viewYear, viewMonth, 1);
      setViewYear(n.year);
      setViewMonth(n.month);
    }
  }, [useJalali, viewMonth, viewYear]);

  const pickDay = useCallback(
    (day: number) => {
      setSelected({
        year: viewYear,
        month: viewMonth,
        day: clampDay(viewYear, viewMonth, day, useJalali),
      });
    },
    [useJalali, viewMonth, viewYear],
  );

  const pickToday = useCallback(() => {
    const next = todayAsPickerDate(useJalali);
    setSelected(next);
    setViewYear(next.year);
    setViewMonth(next.month);
  }, [useJalali]);

  const isDateAllowed = useCallback((date: CalendarPickerDate) => {
    const iso = jalaliKeyToIso(pickerDateToJalaliKey(date, useJalali));
    return (!min || iso >= min) && (!max || iso <= max);
  }, [min, max, useJalali]);

  const confirm = useCallback(() => {
    if (!isDateAllowed(selected)) return;
    onConfirm(pickerDateToJalaliKey(selected, useJalali));
    onClose();
  }, [onClose, onConfirm, selected, useJalali, isDateAllowed]);

  const yearIndex = Math.max(0, yearOptions.indexOf(String(selected.year)));
  const monthIndex = Math.max(0, selected.month - 1);
  const dayIndex = Math.max(0, selected.day - 1);

  return (
    <MobileSheet
      portalContainer={portalContainer}
      open={open}
      onClose={onClose}
      title={t('calendar.datePicker.title', 'Jump to date')}
      subtitle={t('calendar.datePicker.jumpTo', 'Jump to…')}
      zIndex={zIndex}
      footer={
        <div className="nm-date-picker__footer">
          <NmButton variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </NmButton>
          <NmButton variant="ghost" onClick={pickToday} disabled={!isDateAllowed(todayAsPickerDate(useJalali))}>
            {t('calendar.today', 'Today')}
          </NmButton>
          <NmButton variant="primary" onClick={confirm} disabled={!isDateAllowed(selected)}>
            {t('common.confirm', 'OK')}
          </NmButton>
        </div>
      }
    >
      <div className="nm-date-picker" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="nm-date-picker__preview">{displayDigits(preview)}</div>

        <div className="nm-date-picker__nav" dir="ltr">
          <button
            type="button"
            className="nm-date-picker__nav-btn"
            onClick={goPrevMonth}
            aria-label={t('calendar.nav.prevMonth', 'Previous month')}
          >
            <NavChevron direction="prev" />
          </button>
          <button
            type="button"
            className="nm-date-picker__title"
            dir={isRtl ? 'rtl' : 'ltr'}
            onClick={() => setWheelMode((v) => !v)}
            aria-expanded={wheelMode}
          >
            {displayDigits(headerTitle)}
            <span className="nm-date-picker__title-hint">{wheelMode ? '▴' : '▾'}</span>
          </button>
          <button
            type="button"
            className="nm-date-picker__nav-btn"
            onClick={goNextMonth}
            aria-label={t('calendar.nav.nextMonth', 'Next month')}
          >
            <NavChevron direction="next" />
          </button>
        </div>

        {wheelMode ? (
          <div className="nm-date-picker__wheels ltr-island">
            <WheelPicker
              options={yearOptions.map(value => displayDigits(value))}
              selected={yearIndex >= 0 ? yearIndex : 10}
              onChange={(idx) => {
                const y = Number(yearOptions[idx]);
                const day = clampDay(y, selected.month, selected.day, useJalali);
                setSelected({ year: y, month: selected.month, day });
                setViewYear(y);
              }}
            />
            <WheelPicker
              options={monthOptions}
              selected={monthIndex}
              onChange={(idx) => {
                const m = idx + 1;
                const day = clampDay(selected.year, m, selected.day, useJalali);
                setSelected({ year: selected.year, month: m, day });
                setViewMonth(m);
              }}
            />
            <WheelPicker
              options={dayOptions.map(value => displayDigits(value))}
              selected={Math.min(dayIndex, dayOptions.length - 1)}
              onChange={(idx) => {
                setSelected({ ...selected, day: idx + 1 });
              }}
            />
          </div>
        ) : (
          <>
            <div className="nm-date-picker__weekdays">
              {weekdayLabels.map((wd, i) => (
                <span key={`${wd}-${i}`} className="nm-date-picker__weekday">
                  {wd}
                </span>
              ))}
            </div>
            <div className="nm-date-picker__grid">
              {gridCells.map((cell) =>
                cell.muted ? (
                  <span key={cell.key} className="nm-date-picker__cell nm-date-picker__cell--muted" />
                ) : (
                  <button
                    key={cell.key}
                    type="button"
                    disabled={!isDateAllowed({ year: viewYear, month: viewMonth, day: cell.day })}
                    className={[
                      'nm-date-picker__cell',
                      cell.today ? 'nm-date-picker__cell--today' : '',
                      cell.selected ? 'nm-date-picker__cell--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => pickDay(cell.day)}
                  >
                    {displayDigits(cell.day)}
                  </button>
                ),
              )}
            </div>
          </>
        )}

        <p className="nm-date-picker__system">
          {useJalali
            ? t('calendar.datePicker.jalali', 'Persian calendar')
            : t('calendar.datePicker.gregorian', 'Gregorian calendar')}
        </p>
      </div>
    </MobileSheet>
  );
}
